/**
 * @typedef {Object} ProductSelection
 * @property {number} id - The unique identifier for the selected product.
 * @property {Price?} price - Price details of the product.
 * @property {number} quantity - The quantity of the product selected.
 * @property {Discount} discount - Discount details applied to the product.
 * @property {BcDiscounts} bcDiscounts - Breakdown of additional discounts.
 * @property {GiftProduct?} giftProduct - Optional gift product details.
 * @property {Availability} availability - Indicates whether the requested quantity of the product is available for purchase.
 * @property {DealType} dealType - Chosen deal
 */

/**
 * @typedef {Object} Price
 * @property {number} basePrice - The base price of the product.
 * @property {number} sellingPrice - The selling price after discounts.
 */

/**
 * @typedef {Object} Discount
 * @property {number} value - The discount value in currency.
 * @property {string} percentage - The discount percentage.
 * @property {string} text - Descriptive text for the discount.
 */

/**
 * @typedef {Object} BcDiscounts
 * @property {number} bcDisc1 - Discount condition 1.
 * @property {number} bcDisc2 - Discount condition 2.
 * @property {number} bcDisc3 - Discount condition 3.
 * @property {number} bcDisc4 - Discount condition 4.
 * @property {number} bcDisc5 - Discount condition 5.
 * @property {number} bcDisc6 - Discount condition 6.
 * @property {number} bcDisc7 - Discount condition 7.
 * @property {number} bcDisc8 - Discount condition 8.
 * @property {number} bcDisc9 - Discount condition 9.
 * @property {number} bcDisc10 - Discount condition 10.
 */

import {DealType} from './DealSelection.model';
import {findItemAvailability} from '../../common/CatalogService';

/**
 * @typedef {Object} GiftProduct
 * @property {number} id - The unique identifier for the gift product.
 * @property {number} quantity - Quantity of the gift product.
 * @property {Price} price - Price details for the gift product.
 * @property {BcDiscounts} bcDiscount - Additional discounts for the gift product.
 */


export const NoBcDiscounts = {
  bcDisc1: 0,
  bcDisc2: 0,
  bcDisc3: 0,
  bcDisc4: 0,
  bcDisc5: 0,
  bcDisc6: 0,
  bcDisc7: 0,
  bcDisc8: 0,
  bcDisc9: 0,
  bcDisc10: 0
};

// TODO: fix
/**
 * @param {Product} productDetails
 * @param {Lot} lot
 * @param quantity
 * @returns {ProductSelection}
 */
export const buildWithLot = (productDetails, lot, quantity) => {
  const price = {
    basePrice: lot.price.value,
    sellingPrice: lot.price.bestPrice
  };
  const discount = {
    value: lot.price.savings * quantity,
    percentage: lot.price.discount,
    text: lot.price.discountLine
  };
  let bcDiscounts = lot.price.bcDiscounts;
  return {
    id: productDetails.id,
    price,
    quantity,
    bcDiscounts,
    discount,
    dealType: DealType.LOT,
    // El lote elegido viaja al carrito y de ahi a BC (oferta de corta caducidad: ese lote, obligatorio)
    lotCode: lot.code,
    availability: buildLotAvailability(productDetails, lot, quantity)
  };
};

const buildLotAvailability = (productDetails, lot, quantity) => {
  const msg = `${lot.stock} unidades disponibles para el lote`;
  if (quantity > lot.stock) {
    return { isAvailable: false, msg }
  }
  return { isAvailable: true, msg };
}

/**
 * @param {Product} productDetails
 * @param {Offer} offer
 * @param {number} quantity
 * @returns {ProductSelection}
 */
export const buildWithOffer = (productDetails, offer, quantity) => {
  return {
    id: productDetails.id,
    price: buildOfferPrice(productDetails, offer),
    discount: buildOfferDiscount(productDetails, offer, quantity),
    bcDiscounts: buildOfferBcDiscount(productDetails, offer),
    quantity,
    availability: findItemAvailability(productDetails, quantity),
    dealType: DealType.OFFER
  };
};

const buildOfferPrice = (product, offer) => {
  return product.price && {
    basePrice: offer.price.value,
    sellingPrice: offer.price.bestPrice
  };
};

const buildOfferDiscount = (product, offer, quantity) => {
  return offer.price && {
    value: offer.price.savings * quantity,
    percentage: offer.price.discount,
    text: offer.price.discountLine
  };
};

const buildOfferBcDiscount = (productDetails, offer) => {
  let result = NoBcDiscounts;
  if (!!offer.price) {
    result = {
      ...NoBcDiscounts,
      ...offer.price.bcDiscounts
    };
  }
  return result;
};

/**
 * @param {Product} product
 * @param {Promo} promo
 * @param {number} quantity
 * @returns {ProductSelection}
 */
export const buildWitPromo = (product, promo, quantity) => {
  let price = {
    sellingPrice: promo.productPrice.bestPrice,
    basePrice: promo.productPrice.value
  };
  const discount = {
    value: promo.productPrice.savings * quantity,
    percentage: promo.productPrice.discount,
    text: promo.productPrice.discountLine
  };
  let bcDiscounts = promo.productPrice.bcDiscounts;
  const giftProduct = {
    id: promo.promoProductId,
    quantity: Math.floor(quantity / promo.minPurchaseForPromo) * promo.stock,
    price: { sellingPrice: promo.price.bestPrice, basePrice: promo.price.value },
    bcDiscounts: promo.price.bcDiscounts
  };
  return {
    id: product.id,
    price,
    quantity,
    bcDiscounts,
    discount,
    giftProduct,
    availability: findItemAvailability(product, quantity),
    dealType: DealType.PROMO
  };
};

/**
 * @param {Product} product
 * @param quantity
 * @returns {ProductSelection}
 */
export const buildWithoutDeal = (product, quantity) => {
  return {
    id: product.id,
    price: buildPrice(product),
    discount: buildDiscount(product, quantity),
    bcDiscounts: buildBcDiscounts(product),
    quantity,
    availability: findItemAvailability(product, quantity),
    dealType: DealType.NONE
  };
};

// Regalo/merchandising "solo modo comercial" sin tarifa en BC: se vende a 0 EUR (el api lo valida
// igual en el checkout). Con tarifa se trata como cualquier otro producto.
export const isFreeComercialOnly = (product) => !!product?.comercialOnly && !product?.price;

/**
 * @param {Product} product
 * @returns {{sellingPrice: (number|number|*), basePrice: (number|number|*)}}
 */
const buildPrice = (product) => {
  if (isFreeComercialOnly(product)) {
    return { basePrice: 0, sellingPrice: 0 };
  }
  const price = product.price;
  return price && {
    basePrice: price.value,
    sellingPrice: price.bestPrice
  };
};

const buildDiscount = (product, quantity) => {
  if (isFreeComercialOnly(product)) {
    return { value: 0, percentage: 0, text: null };
  }
  const price = product.price;
  return price && {
    value: price.savings * quantity,
    percentage: price.discount,
    text: price.discountLine
  };
};

const buildBcDiscounts = (product) => {
  const price = product.price;
  let result = NoBcDiscounts;
  if (!!price) {
    result = {
      ...product.price.bcDiscounts,
      bcDisc9: price.bcDisc9,
      bcDisc10: price.bcDisc10
    };
  }
  return result;
};

// Las promos X+Y se compran por packs completos: la cantidad va siempre en múltiplos de minPurchaseForPromo
const roundUpToChunk = (quantity, chunk) => {
  const n = Number(quantity) || 0;
  if (!(chunk > 0)) {
    return n;
  }
  return Math.max(chunk, Math.ceil(n / chunk) * chunk);
};

/**
 * Cantidad válida para el trato al elegirlo (promo X+Y: múltiplo superior).
 * @param {DealSelection|null} deal
 * @param {number} quantity
 */
export const normalizeQuantityForDeal = (deal, quantity) =>
  deal?.type === DealType.PROMO ? roundUpToChunk(quantity, deal.value.minPurchaseForPromo) : quantity;

/**
 * Cantidad nueva ajustada al trato: con promo X+Y sube o baja al múltiplo según el sentido del cambio.
 * @param {DealSelection|null} deal
 * @param {number} prevQuantity
 * @param {number} newQuantity
 */
export const adjustQuantityToDeal = (deal, prevQuantity, newQuantity) => {
  if (deal?.type !== DealType.PROMO) {
    return newQuantity;
  }
  const chunk = deal.value.minPurchaseForPromo;
  if (!(chunk > 0)) {
    return newQuantity;
  }
  const n = Number(newQuantity) || 0;
  const prev = roundUpToChunk(prevQuantity, chunk);
  if (n < prev) {
    return Math.max(chunk, Math.floor(n / chunk) * chunk);
  }
  return roundUpToChunk(n, chunk);
};

/**
 * @param {Product} product
 * @param {DealSelection|null} deal
 * @param {number} quantity
 * @returns {ProductSelection}
 */
export const buildProductSelection = (product, deal, quantity) => {
  switch (deal?.type) {
    case DealType.LOT:
      return buildWithLot(product, deal.value, quantity);
    case DealType.PROMO:
      return buildWitPromo(product, deal.value, roundUpToChunk(quantity, deal.value.minPurchaseForPromo));
    case DealType.OFFER:
      return buildWithOffer(product, deal.value, Math.max(Number(quantity) || 0, deal.value.minQuantity));
    default:
      return buildWithoutDeal(product, quantity);
  }
};

/**
 * Mismo trato en una ficha recargada (los objetos de lots/promos/offers cambian de identidad).
 * @param {Product} product
 * @param {DealSelection|null} deal
 * @returns {DealSelection|null}
 */
export const findEquivalentDeal = (product, deal) => {
  let found;
  switch (deal?.type) {
    case DealType.LOT:
      found = (product?.lots ?? []).find(lot => lot.code === deal.value.code);
      break;
    case DealType.PROMO:
      found = (product?.promos ?? []).find(promo => promo.promoProductId === deal.value.promoProductId
        && promo.minPurchaseForPromo === deal.value.minPurchaseForPromo);
      break;
    case DealType.OFFER:
      found = (product?.offers ?? []).find(offer => offer.minQuantity === deal.value.minQuantity);
      break;
    default:
      return null;
  }
  if (!found) {
    return null;
  }
  return found === deal.value ? deal : { type: deal.type, value: found };
};

// Mismo criterio que el servidor al añadir (CartServiceImpl.mejorPrecioParaCantidad): la oferta por
// cantidad alcanzada con menor precio final; si ninguna mejora, el precio unitario.
/**
 * @param {Object|null} unitPrice - product.price
 * @param {Offer[]} offers
 * @param {number} quantity
 * @returns {Object|null} precio con value/bestPrice
 */
export const bestPriceForQuantity = (unitPrice, offers, quantity) => {
  if (!unitPrice) {
    return null;
  }
  return (offers ?? [])
    .filter(offer => offer.price && offer.minQuantity <= quantity)
    .reduce((best, offer) => ((offer.price.bestPrice ?? 0) < (best.bestPrice ?? 0) ? offer.price : best), unitPrice);
};

/**
 * Siguiente oferta por cantidad que mejora el precio actual (la de menor cantidad mínima).
 * @returns {Offer|null}
 */
export const nextQuantityOffer = (unitPrice, offers, quantity) => {
  const current = bestPriceForQuantity(unitPrice, offers, quantity);
  if (!current) {
    return null;
  }
  return (offers ?? [])
    .filter(offer => offer.price && offer.minQuantity > quantity && (offer.price.bestPrice ?? 0) < (current.bestPrice ?? 0))
    .reduce((next, offer) => (!next || offer.minQuantity < next.minQuantity
      || (offer.minQuantity === next.minQuantity && offer.price.bestPrice < next.price.bestPrice) ? offer : next), null);
};
